import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch } from '@/utils/typeHelpers'
import { getIsFetching } from '@/src/reducers/fetch'
import { useReselector } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { sumBy } from 'lodash'

import {
  Button,
  Card,
  Text,
  Alert,
  Progress,
  CardColors,
  Separator,
  Table,
  Icon,
  Banner,
  Loader,
} from '@/ui'
import { PageHeader } from '@/components/PageHeader'

import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { AllocationRuleForm } from './AllocationRuleForm'
import { fetchAllocationGuidesIfNeeded } from '@/src/actions/allocationGuideActions'
import {
  fetchAllocationRulesIfNeeded,
  editAllocationRuleset,
  FETCH_ALLOCATION_RULES_KEY,
} from '@/src/actions/allocationRuleActions'
import {
  ExpensesSummary,
  generateSummary,
} from '@/src/services/allocationGuideService'
import { getAllocationRuleArray } from '@/src/selectors/allocationGuideSelectors'
import { AllocationRule } from '@/src/reducers/finances/allocationRulesReducer'
import { DEFAULT_ALLOCATION_RULES } from './AllocationConstants'

import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import {
  PostOnboardingTaskIdents,
  useIsOnboardingNav,
} from '@/features/onboarding/config'
import { TextColors, TypographyStyles } from '@/styles'
import { fa1 } from '@fortawesome/pro-regular-svg-icons'
const MonthlySummary = ({
  generatedSummaries,
  loading,
}: {
  generatedSummaries: ExpensesSummary | null
  loading: boolean
}) => {
  const avg = generatedSummaries?.average
  const summaries = generatedSummaries?.summaries

  if (loading) {
    return (
      <div className="centered">
        <Loader inline active />
      </div>
    )
  }
  if (avg?.noData) {
    return (
      <Card>
        <Text variant={TypographyStyles.H3} className="mb-4">
          It looks like you&apos;re new here!
        </Text>
        <Text variant={TypographyStyles.BODY_MD}>
          It looks like this is your first month with Heard! This means that we
          don&apos;t have any historical data (yet) to present here, but we
          encourage you to still set up your allocations. You can always revisit
          and make changes as we learn more about your business.
        </Text>
      </Card>
    )
  }

  /* This means this is a user with no data yet*/

  return (
    <>
      <Banner
        text="The numbers you see below are based off your categorized
        transactions at the current time, and may not be reflective of all
        transactions from your accounts. We suggest using it purely as an
        estimate."
        className="mb-4 mt-4"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaries?.map((summary) => (
          <Card
            key={`summary-row-${summary.month}`}
            color={CardColors.LEVEL_1}
            className="p-6 w-full"
          >
            <div className="text-center">
              <Text variant={TypographyStyles.LABEL_MD}>{summary.month}</Text>
              <br />
              {summary.noData ? (
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                >
                  It looks like we have no data for this month.
                </Text>
              ) : (
                <>
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="flex justify-between mb-2"
                  >
                    Income
                    <CurrencyFormatLabel value={summary.income} />
                  </Text>
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="flex justify-between mb-2"
                  >
                    Expenses
                    <CurrencyFormatLabel value={summary.expenses} />
                  </Text>
                  <Text
                    variant={TypographyStyles.BODY_SM}
                    className="flex justify-between font-semibold"
                  >
                    Net Profit
                    <CurrencyFormatLabel
                      value={summary.income - summary.expenses}
                    />
                  </Text>
                </>
              )}
            </div>
          </Card>
        ))}

        <Card className="p-6 w-full" color={CardColors.LEVEL_1}>
          <Text variant={TypographyStyles.LABEL_MD}>
            Average from past 3 months
          </Text>
          <br />
          <Text
            variant={TypographyStyles.BODY_SM}
            className="flex justify-between"
          >
            Average Income
            <CurrencyFormatLabel value={avg?.income} />
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            className="flex justify-between"
          >
            Average Expenses
            <CurrencyFormatLabel value={avg?.expenses} />
          </Text>
          <Text
            variant={TypographyStyles.BODY_SM}
            className="flex justify-between font-semibold"
          >
            Average Profit
            <CurrencyFormatLabel
              value={(avg?.income ?? 0) - (avg?.expenses ?? 0)}
            />
          </Text>
        </Card>
      </div>
    </>
  )
}

/*
  Form used to create OR update a user's allocation guide
*/
const AllocationUpdateForm = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const isOnboarding = useIsOnboardingNav()

  const existingAllocationRules = useReselector(getAllocationRuleArray)
  const isFetchingAllocationRules = useReselector(
    getIsFetching,
    FETCH_ALLOCATION_RULES_KEY
  )

  const [step, setStep] = useState(0)
  const [allocationRules, setAllocationRules] = useState<AllocationRule[]>(
    existingAllocationRules
  )
  const [percentageSum, setPercentageSum] = useState(0)
  const [generatingSummary, setGeneratingSummary] = useState(true)
  const [summaries, setSummaries] = useState<ExpensesSummary | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submissionError, setSubmissionError] = useState(false)

  const handlePercentageSummation = useCallback(() => {
    setPercentageSum(
      sumBy(allocationRules, (rule) => Number(rule.percentage) || 0)
    )
  }, [allocationRules])

  useEffect(() => {
    if (existingAllocationRules.length) {
      setAllocationRules(existingAllocationRules)
    }
  }, [existingAllocationRules])

  useEffect(() => {
    async function fetchData() {
      const generated = await generateSummary()
      setSummaries(generated)
      setGeneratingSummary(false)
    }

    fetchData()
  }, [])

  useEffect(() => {
    handlePercentageSummation()
  }, [allocationRules, handlePercentageSummation])

  const track = useAnalyticsTrack()

  const submitAllocationRules = async () => {
    track('submitted allocation guide edit')
    setSubmitting(true)
    const successfulUpdate =
      await editAllocationRuleset(allocationRules)(dispatch)
    if (!successfulUpdate) {
      setSubmissionError(true)
    } else {
      markUserActionItemCompleteIfExists(
        PostOnboardingTaskIdents.SET_UP_HOW_YOUR_EARNINGS_ARE_ALLOCATED,
        (event, properties) => track(event, properties)
      )
      setStep(2)
    }
  }

  const renderProgress = () => {
    return (
      <Progress
        variant="segmented"
        numberOfSegments={2}
        activeSegment={step + 1} // assume `step` is 0-based
        segmentTitles={[
          '1. Determine your monthly allocations',
          '2. Review and confirm',
        ]}
      />
    )
  }

  if (step === 2) {
    return (
      <>
        {renderProgress()}
        <br />
        <Card color={CardColors.SUCCESS}>
          <Text variant={TypographyStyles.H3} className="mb-4">
            Congrats, you&#39;ve successfully set up your Allocation Guide!
            You&#39;re on your way to organizing your practice finances!
          </Text>
          <Text variant={TypographyStyles.BODY_MD}>
            Your next allocation guide will be sent to you{' '}
            <b>on the 15th of the next month</b>, after we&#39;ve gone through
            and complete your books. In this allocation guide, we will provide
            suggested amounts to allocate based on your previous months&apos;
            earnings and your allocation preferences.
          </Text>
          <br />
          <Button
            onClick={() => navigate(isOnboarding ? '/' : '/allocation-guides')}
          >
            Back to {isOnboarding ? 'Allocations' : 'Dashboard'}
          </Button>
        </Card>
      </>
    )
  } else if (step === 1) {
    return (
      <>
        {renderProgress()}
        <br />
        <Text variant={TypographyStyles.BODY_MD} className="my-6">
          Review your allocation preferences below, then confirm to apply them
          to your account. You can adjust your allocations at any time based on
          your personal preferences.
        </Text>
        <Card className="p-6 my-4 w-2/3 mx-auto" color={CardColors.LEVEL_1}>
          <Text variant={TypographyStyles.H2}>Your Monthly Allocations</Text>

          {submissionError && (
            <Alert
              variant="error"
              title="We're having difficulties saving your allocation guide! Please try again."
              className="my-4"
            />
          )}

          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell className="w-5/6">
                  Allocation Type
                </Table.HeaderCell>
                <Table.HeaderCell className="w-1/6 text-right">
                  Percentage of Profit to Allocate
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {allocationRules.map((rule) => (
                <Table.Row key={`review-rule-row-${rule.ruleName}`}>
                  <Table.Cell>{rule.ruleName}</Table.Cell>
                  <Table.Cell className="text-right">
                    {rule.percentage}%
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>

            <Table.Footer>
              <Table.Row>
                <Table.Cell className="font-semibold text-right">
                  Total % of Profit Allocated:
                </Table.Cell>
                <Table.Cell className="font-semibold text-right">
                  {percentageSum}%
                </Table.Cell>
              </Table.Row>
            </Table.Footer>
          </Table>
        </Card>

        <Separator />

        <div className="flex justify-between items-center mt-4">
          <Button onClick={() => setStep(0)} variant="secondary">
            Back
          </Button>
          <Button
            disabled={submitting}
            onClick={submitAllocationRules}
            loading={submitting}
            id="btn-allocation-guide-creation-confirm"
            className="text-right"
          >
            Confirm &amp; Set
          </Button>
        </div>
      </>
    )
  }
  return (
    <>
      {renderProgress()}
      <br />
      <Text variant={TypographyStyles.BODY_SM} className="my-4">
        Set up your Financial Allocation Guide to decide how much money to set
        aside for taxes, retirement, paychecks and more. On the 15th of every
        month, we&apos;ll send over a &ldquo;paystub&rdquo; with suggested
        dollar amounts, based on your previous months&apos; earnings.
      </Text>
      <Card color={CardColors.LEVEL_0} className="my-6">
        <Text
          variant={TypographyStyles.H2}
          className="mt-4 mb-4 inline-flex items-center"
        >
          <Icon
            icon={fa1}
            backgroundColor="level2"
            backgroundStyle="circle"
            size="1x"
          />
          Review your last 3 months&apos; earnings
        </Text>
        <MonthlySummary
          generatedSummaries={summaries}
          loading={generatingSummary}
        />
      </Card>

      {isFetchingAllocationRules ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Loader inline active />
        </div>
      ) : (
        <AllocationRuleForm
          initialRules={
            existingAllocationRules.length !== 0
              ? existingAllocationRules
              : DEFAULT_ALLOCATION_RULES
          }
          percentageSum={percentageSum}
          progressForward={() => setStep(1)}
          updateRules={setAllocationRules}
        />
      )}
    </>
  )
}

export const AllocationCreation = () => {
  const { state } = useParams<{ state: string }>()
  const dispatch = useAppDispatch()

  const edit = useMemo(() => state !== 'new', [state])

  useEffect(() => {
    dispatch(fetchAllocationGuidesIfNeeded())
    dispatch(fetchAllocationRulesIfNeeded())
  }, [dispatch])

  return (
    <div id="allocationCreationPanel">
      <PageHeader
        header={
          edit
            ? 'Edit your monthly allocation guide'
            : 'Set up your monthly allocation guide'
        }
      />
      <Card color={CardColors.LEVEL_0} className="mt-4">
        <AllocationUpdateForm />
      </Card>
    </div>
  )
}
